// Browser web-push (Firebase Cloud Messaging) client for the messagerie.
//
// "On" registers this device's FCM token with the backend (asking notification
// permission if needed) so heric-back pushes new-message notifications, which
// the service worker shows as a system pop-up even when the user is on another
// tab. "Off" unregisters and deletes the token.
//
// The per-device on/off intent is persisted in localStorage so the toggle
// reflects what the user last chose on this browser. Everything degrades
// gracefully: unsupported browsers or a missing VAPID key yield status
// "unsupported" and make enable() a no-op (never crashes).

import { useCallback, useEffect, useRef, useState } from "react";
import {
  getToken,
  deleteToken,
  onMessage,
  type MessagePayload,
} from "firebase/messaging";
import firebase from "@services/firebase";
import { pushApi } from "@services/api/push";

const WEBPUSH_INTENT_KEY = "heric_messagerie_webpush";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export type WebPushStatus =
  | "loading"
  | "on"
  | "off"
  | "denied"
  | "unsupported";

/** Basic browser capability check (no permission / config involved). */
function browserSupportsPush(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/** Everything required for web-push to actually work, including the VAPID key. */
function isConfigured(): boolean {
  return browserSupportsPush() && !!VAPID_KEY;
}

function readIntent(): boolean {
  try {
    return localStorage.getItem(WEBPUSH_INTENT_KEY) === "1";
  } catch {
    return false;
  }
}

function writeIntent(on: boolean): void {
  try {
    if (on) localStorage.setItem(WEBPUSH_INTENT_KEY, "1");
    else localStorage.removeItem(WEBPUSH_INTENT_KEY);
  } catch {
    /* ignore storage errors (private mode) */
  }
}

/** Build the SW registration URL carrying the firebase config in the query string. */
function swRegistrationUrl(): string {
  const params = new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY ?? "",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? "",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ?? "",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ?? "",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? "",
    appId: process.env.REACT_APP_FIREBASE_APP_ID ?? "",
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register(swRegistrationUrl());
}

/** A best-effort device label for the backend (device_name field). */
function deviceName(): string {
  if (typeof navigator === "undefined") return "Navigateur";
  const ua = navigator.userAgent;
  const browser =
    /Edg\//.test(ua) ? "Edge"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari"
    : "Navigateur";
  const os =
    /Windows/.test(ua) ? "Windows"
    : /Mac OS X/.test(ua) ? "macOS"
    : /Linux/.test(ua) ? "Linux"
    : /Android/.test(ua) ? "Android"
    : /iPhone|iPad/.test(ua) ? "iOS"
    : "";
  return os ? `${browser} sur ${os}` : browser;
}

/** Get this browser's FCM token (registers the SW if needed). null on failure. */
async function fetchDeviceToken(): Promise<string | null> {
  if (!isConfigured()) return null;
  if (Notification.permission !== "granted") return null;
  try {
    const messaging = await firebase.getMessaging();
    if (!messaging) return null;
    const serviceWorkerRegistration = await registerServiceWorker();
    return await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    });
  } catch (e) {
    console.warn("[messagerie] getToken failed:", e);
    return null;
  }
}

/**
 * Turn web-push ON for this device: request permission, obtain the FCM token,
 * and register it with the backend. Returns the resulting status.
 */
export async function enableWebPush(): Promise<WebPushStatus> {
  if (!isConfigured()) return "unsupported";
  if (Notification.permission === "denied") return "denied";

  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();
    if (permission === "denied") return "denied";
    if (permission !== "granted") return "off";
  }

  const token = await fetchDeviceToken();
  if (!token) return "off";

  await pushApi.registerToken({
    token,
    platform: "web",
    device_name: deviceName(),
    app_version: process.env.REACT_APP_ENV ?? undefined,
  });
  writeIntent(true);
  return "on";
}

/**
 * Turn web-push OFF for this device: unregister the token on the backend and
 * delete it locally. Best-effort — always clears the local intent.
 */
export async function disableWebPush(): Promise<WebPushStatus> {
  writeIntent(false);
  if (!isConfigured()) return "unsupported";
  try {
    const token = await fetchDeviceToken();
    if (token) {
      try {
        await pushApi.unregisterToken(token);
      } catch (e) {
        console.warn("[messagerie] unregisterToken failed:", e);
      }
      const messaging = await firebase.getMessaging();
      if (messaging) {
        try {
          await deleteToken(messaging);
        } catch (e) {
          console.warn("[messagerie] deleteToken failed:", e);
        }
      }
    }
  } catch (e) {
    console.warn("[messagerie] disableWebPush failed:", e);
  }
  return Notification.permission === "denied" ? "denied" : "off";
}

// Attach the foreground onMessage listener at most once per page load, even if
// the hook mounts in several places — otherwise we'd show duplicate pop-ups.
let foregroundListenerAttached = false;

async function attachForegroundListener(
  isViewingDiscussion: (discussionId?: string) => boolean,
): Promise<void> {
  if (foregroundListenerAttached) return;
  if (!isConfigured()) return;
  const messaging = await firebase.getMessaging();
  if (!messaging) return;
  foregroundListenerAttached = true;

  onMessage(messaging, (payload: MessagePayload) => {
    // Don't double-notify the discussion the user is actively reading.
    const discussionId =
      payload.data?.discussion_id || payload.data?.discussionId;
    if (isViewingDiscussion(discussionId)) return;
    if (Notification.permission !== "granted") return;

    const title = payload.notification?.title || payload.data?.title || "Nouveau message";
    const body = payload.notification?.body || payload.data?.body || "";

    navigator.serviceWorker.ready
      .then((reg) =>
        reg.showNotification(title, {
          body,
          icon: "/heric_app_logo.png",
          badge: "/heric_app_logo.png",
          tag: payload.data?.tag || "heric-messagerie",
          data: {
            ...(payload.data || {}),
            url: payload.data?.url || payload.data?.route || "/messagerie",
          },
        }),
      )
      .catch(() => {
        /* best-effort */
      });
  });
}

export interface UseWebPushNotifications {
  status: WebPushStatus;
  working: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

/**
 * React hook backing the messagerie notifications toggle. `isViewingDiscussion`
 * (optional) lets foreground notifications skip the discussion currently on
 * screen, avoiding a double-notify.
 */
export function useWebPushNotifications(
  isViewingDiscussion?: (discussionId?: string) => boolean,
): UseWebPushNotifications {
  const [status, setStatus] = useState<WebPushStatus>("loading");
  const [working, setWorking] = useState(false);
  const viewingRef = useRef(isViewingDiscussion);
  viewingRef.current = isViewingDiscussion;

  const refresh = useCallback(async () => {
    if (!isConfigured()) {
      setStatus("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    if (Notification.permission !== "granted") {
      // Can't be "on" without permission; reflect the stored intent as off.
      setStatus("off");
      return;
    }
    setStatus(readIntent() ? "on" : "off");
  }, []);

  useEffect(() => {
    refresh();
    attachForegroundListener((id) => viewingRef.current?.(id) ?? false);
  }, [refresh]);

  const enable = useCallback(async () => {
    if (working || !isConfigured()) {
      if (!isConfigured()) setStatus("unsupported");
      return;
    }
    setWorking(true);
    try {
      const next = await enableWebPush();
      setStatus(next);
    } catch (e) {
      console.warn("[messagerie] enable web-push failed:", e);
      setStatus(Notification.permission === "denied" ? "denied" : "off");
    } finally {
      setWorking(false);
    }
  }, [working]);

  const disable = useCallback(async () => {
    if (working) return;
    setWorking(true);
    try {
      const next = await disableWebPush();
      setStatus(next);
    } catch (e) {
      console.warn("[messagerie] disable web-push failed:", e);
    } finally {
      setWorking(false);
    }
  }, [working]);

  return { status, working, enable, disable };
}
